import { Injectable, signal } from '@angular/core';

/** Severity variants for a toast notification. */
export type ToastType = 'error' | 'success' | 'info';

/** A single active toast notification. */
export interface Toast {
  readonly id: number;
  readonly message: string;
  readonly type: ToastType;
  readonly delay: number;
}

const DEFAULT_DELAY_MS = 5_000;

/** Monotonically increasing counter — never resets, so IDs are globally unique. */
let _nextId = 0;

/**
 * Application-level service for queuing transient toast notifications.
 *
 * Provides a readonly Signal of all active toasts consumed by ToastContainerComponent.
 * Callers enqueue toasts via `show`; the container removes them via `remove`
 * when the NgbToast `(hidden)` event fires.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts = signal<Toast[]>([]);

  /** Read-only signal of all currently active toasts. */
  readonly toasts = this._toasts.asReadonly();

  /**
   * Enqueues a new toast notification.
   * @param message Text to display in the toast body.
   * @param type    Visual severity. Defaults to `'error'`.
   * @param delay   Auto-hide delay in ms. Defaults to 5000.
   */
  show(message: string, type: ToastType = 'error', delay: number = DEFAULT_DELAY_MS): void {
    this._toasts.update(current => [...current, { id: ++_nextId, message, type, delay }]);
  }

  /**
   * Removes the toast with the given id from the active list.
   * Called by ToastContainerComponent via the NgbToast `(hidden)` output.
   * @param id The toast id to remove.
   */
  remove(id: number): void {
    this._toasts.update(current => current.filter(t => t.id !== id));
  }
}
